import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ClompApi } from '../api/ClompApi';
import { timeAgo } from '../api/presence';
import './AdminPage.css';

const api = new ClompApi();

// The panel's pages, listed down its side. Add new ones here and give them a render method below.
const SECTIONS = [
    { id: 'users', name: 'Users' }
];

function formatDate(value) {
    return value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '–';
}

export class AdminPage extends React.Component {

    state = {
        loading: true,
        // 'hidden' when the viewer isn't allowed in (the page then looks like any missing page), 'error' if it
        // couldn't load
        problem: null,
        open: false,
        // the viewer's own user id, to mark their row; null in open testing mode
        viewerID: null,
        levels: [],
        users: [],
        search: '',
        // userID -> 'saving' | 'saved' | 'failed'
        status: {},
        // the id of the user whose Delete button has been pressed and is waiting to be confirmed, the one being
        // deleted, and the last thing that went wrong with a delete
        confirmingDelete: null,
        deleting: null,
        deleteError: null
    };

    componentDidMount() {
        let hadLogin = localStorage.getItem('token') !== null;
        api.admin.getUsers().then((data) => {
            this.setState({ loading: false, open: data.open, viewerID: data.viewerID, levels: data.levels, users: data.users });
        }).catch((err) => {
            let code = err.response && err.response.status;
            // Nothing here should hint that an admin panel lives at this address: logged-out visitors go straight
            // to the login page, and accounts without access see the same thing as any page that doesn't exist.
            if (code === 401) {
                // A login that has run out comes back here after logging in again; someone who was never logged in
                // gets the plain login page.
                this.props.history.replace(hadLogin ? '/login?next=/admin' : '/login');
                return;
            }
            if (code !== 403) {
                console.log('Error loading page: ', err);
            }
            this.setState({ loading: false, problem: code === 403 ? 'hidden' : 'error' });
        });
    }

    setLevel(user, level) {
        let before = user.level;
        let mark = (state) => this.setState((s) => ({ status: { ...s.status, [user.id]: state } }));
        let show = (value, more) => this.setState((s) => ({ users: s.users.map((u) => (u.id === user.id ? { ...u, ...more, level: value } : u)) }));
        show(level);
        mark('saving');
        api.admin.setLevel(user.id, level).then((result) => {
            // what the viewer may do with this user can change with the user's level
            show(result.user.level, { canSet: result.user.canSet });
            mark('saved');
        }).catch((err) => {
            console.log('Error changing access level: ', err);
            show(before);
            mark('failed');
        });
    }

    deleteUser(user) {
        this.setState({ deleting: user.id, deleteError: null });
        api.admin.deleteUser(user.id).then(() => {
            this.setState((s) => ({ users: s.users.filter((u) => u.id !== user.id), deleting: null, confirmingDelete: null }));
        }).catch((err) => {
            console.log('Error deleting account: ', err);
            let message = err.response && err.response.data && err.response.data.message;
            this.setState({ deleting: null, confirmingDelete: null, deleteError: `Couldn't delete ${user.username}${message ? `: ${message}` : '. Please try again.'}` });
        });
    }

    // The last cell of a user's row: a Delete button, which asks to be pressed again before anything is deleted
    renderDelete(user) {
        let { confirmingDelete, deleting, viewerID } = this.state;
        if (!user.canDelete) {
            let why = user.id === viewerID ? 'Your own account is deleted from your profile settings' : "You aren't allowed to delete this account";
            return <button type="button" className="adm-delete" disabled title={why}>Delete</button>;
        }
        if (confirmingDelete === user.id) {
            return (
                <span className="adm-confirm" role="group" aria-label={`Delete ${user.username}?`}>
                    <button type="button" className="adm-delete adm-delete-sure" disabled={deleting === user.id} onClick={() => this.deleteUser(user)}>
                        {deleting === user.id ? 'Deleting...' : 'Delete for good'}
                    </button>
                    <button type="button" className="adm-cancel" disabled={deleting === user.id} onClick={() => this.setState({ confirmingDelete: null })}>Cancel</button>
                </span>
            );
        }
        return <button type="button" className="adm-delete" disabled={deleting !== null} aria-label={`Delete ${user.username}'s account`} onClick={() => this.setState({ confirmingDelete: user.id, deleteError: null })}>Delete</button>;
    }

    renderUsers() {
        let { users, levels, search, status, viewerID } = this.state;
        // each user's canSet lists the levels the viewer may give them; the backend decides and enforces it
        let canEdit = (user) => (user.canSet || []).some((level) => level !== user.level);
        let lockReason = (user) => (user.id === viewerID ? "You can't change your own access level" : "You aren't allowed to change this user's level");
        let wanted = search.trim().toLowerCase();
        let shown = users.filter((user) => !wanted || user.username.toLowerCase().includes(wanted) || String(user.id) === wanted);
        return (
            <section>
                <div className="adm-head">
                    <h2>Users</h2>
                    <input className="adm-search" type="search" aria-label="Search users" placeholder="Search by username or id"
                        value={search} onChange={(e) => this.setState({ search: e.target.value })} />
                </div>
                <ul className="adm-counts">
                    <li><strong>{users.length}</strong> signed up</li>
                    <li className="adm-count-online"><strong>{users.filter((user) => user.online).length}</strong> online now</li>
                    {levels.map((level) => (
                        <li key={level.id}><strong>{users.filter((user) => user.level === level.id).length}</strong> {level.name}</li>
                    ))}
                </ul>
                {this.state.deleteError ? <p className="adm-error" role="alert">{this.state.deleteError}</p> : null}
                <div className="adm-table-wrap">
                    <table className="adm-table">
                        <thead>
                            <tr>
                                <th className="adm-id">ID</th>
                                <th>Username</th>
                                <th>Joined</th>
                                <th>Status</th>
                                <th>Access level</th>
                                <th>Account</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((user) => (
                                <tr key={user.id}>
                                    <td className="adm-id">{user.id}</td>
                                    <td>
                                        <Link className="adm-user" to={`/profile/${encodeURIComponent(user.username)}`}>{user.username}</Link>
                                        {user.id === viewerID ? <span className="adm-you">You</span> : null}
                                    </td>
                                    <td>{formatDate(user.joined)}</td>
                                    <td>
                                        {user.online ?
                                            <span className="adm-presence is-online"><span className="adm-presence-dot" aria-hidden="true"></span>Online</span>
                                            :
                                            <span className="adm-presence" title={user.lastSeen ? new Date(user.lastSeen).toLocaleString() : undefined}>
                                                <span className="adm-presence-dot" aria-hidden="true"></span>{user.lastSeen ? `Seen ${timeAgo(user.lastSeen)}` : 'Never seen'}
                                            </span>
                                        }
                                        {user.presenceHidden ? <span className="adm-hidden" title="This user hides their status from other players">hidden</span> : null}
                                    </td>
                                    <td>
                                        <select className={`adm-level adm-level-${user.level}`} aria-label={`Access level for ${user.username}`} value={user.level}
                                            disabled={status[user.id] === 'saving' || !canEdit(user)}
                                            title={canEdit(user) ? undefined : lockReason(user)}
                                            onChange={(e) => this.setLevel(user, Number(e.target.value))}>
                                            {levels.map((level) => (
                                                <option key={level.id} value={level.id} disabled={level.id !== user.level && !(user.canSet || []).includes(level.id)}>{level.name}</option>
                                            ))}
                                        </select>
                                        <span className={`adm-status adm-status-${status[user.id] || 'none'}`} role="status">
                                            {status[user.id] === 'saving' ? 'Saving...' : status[user.id] === 'saved' ? 'Saved' : status[user.id] === 'failed' ? "Couldn't save" : ''}
                                        </span>
                                    </td>
                                    <td>{this.renderDelete(user)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {shown.length === 0 ?
                    <p className="adm-empty">{users.length === 0 ? 'Nobody has signed up yet.' : 'No users match that search.'}</p>
                    : null}
            </section>
        );
    }

    render() {
        let { loading, problem } = this.state;
        // Until the backend has said the viewer may come in, show nothing that names the panel
        if (loading) {
            return <Navbar history={this.props.history}></Navbar>;
        }
        if (problem === 'hidden') {
            // the same as NotFoundPage
            return (
                <>
                    <Navbar history={this.props.history}></Navbar>
                    <div>
                        <h1>404: Page not found</h1>
                    </div>
                </>
            );
        }
        if (problem) {
            return (
                <>
                    <Navbar history={this.props.history}></Navbar>
                    <div className="adm-page">
                        <p className="adm-note">Couldn't load this page right now. Please try again later.</p>
                    </div>
                </>
            );
        }
        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className="adm-page">
                    <div className="adm-page-header">
                        <p className="adm-eyebrow">Clomp</p>
                        <h1>Admin panel</h1>
                    </div>
                    {this.state.open ?
                        <p className="adm-warning" role="note">
                            <strong>Testing mode:</strong> this panel is open to everyone, logged in or not. Never run the live server this way.
                        </p>
                        : null}
                    <div className="adm-layout">
                        <nav className="adm-nav" aria-label="Admin pages">
                            {SECTIONS.map((section) => (
                                <Link key={section.id} className="is-active" aria-current="page" to={`/admin/${section.id}`}>{section.name}</Link>
                            ))}
                        </nav>
                        <div className="adm-main">
                            {this.renderUsers()}
                        </div>
                    </div>
                </div>
            </>
        );
    }

}
